import { message } from "antd";
import { useState } from "react";

import { v4 as uuidv4 } from "uuid";
import { ClassificationAction, ClassificationStatus } from "../utils";

export const useClassificationActions = (
	data,
	updateData,
	addPendingOperation,
	clearPendingOperations,
	getSelectedIds,
	setSelectedItems,
	operations
) => {
	const [selectedAction, setSelectedAction] = useState(
		ClassificationAction.EMPTY
	);
	const { applyOperationsToTree, removeNodesById, flattenClassifications } = operations;

	// Resetear datos
	const resetData = (backupData) => {
		if (JSON.stringify(data) === JSON.stringify(backupData)) {
			message.info("No se realizaron cambios para restaurar");
			return;
		}
		updateData(backupData);
		clearPendingOperations();
		setSelectedItems({});
		message.success("Cambios restaurados");
	};

	// Crear nueva categoría
	const handleCreateCategory = (newCategory) => {
		const newCategoryData = {
			id: uuidv4(),
			classification_name: newCategory,
			classification: newCategory,
			sort_order: data.length + 1,
			depth: 0,
			parent_id: null,
			children_ids: [],
			children: [],
			status: ClassificationStatus.NEW,
			archived: false,
			isInUse: false,
		};
		updateData([...data, newCategoryData]);
		addPendingOperation("CREATE", newCategoryData.id, newCategoryData);
		message.success(`Categoría "${newCategory}" creada exitosamente`);
	};

	// Manejar acciones seleccionadas
	const handleActionSelected = (action) => {
		if (action === ClassificationAction.EMPTY) return;
		const selectedIds = getSelectedIds();
		if (selectedIds.length === 0) {
			message.warning(
				"Por favor, seleccione al menos un elemento para realizar esta acción"
			);
			return;
		}

		switch (action) {
		case ClassificationAction.ARCHIVE:
			processArchiveAction(selectedIds);
			break;
		case ClassificationAction.UNARCHIVE:
			processUnarchiveAction(selectedIds);
			break;
		case ClassificationAction.DELETE:
			processDeleteAction(selectedIds);
			break;
		default:
			break;
		}

		setSelectedAction(ClassificationAction.EMPTY);
		setSelectedItems({});
	};

	const processArchiveAction = (selectedIds) => {
		const { updatedTree, processedIds } = applyOperationsToTree(
			data,
			selectedIds,
			"ARCHIVE"
		);

		updateData(updatedTree);
		if (processedIds.length > 0) {
			addPendingOperation("ARCHIVE", processedIds);
			message.success(
				`${processedIds.length} elemento${processedIds.length !== 1 ? "s" : ""} archivado${processedIds.length !== 1 ? "s" : ""}`
			);
		} else {
			message.info("No se archivó ningún elemento");
		}
	};

	const processUnarchiveAction = (selectedIds) => {
		const { updatedTree, processedIds } = applyOperationsToTree(
			data,
			selectedIds,
			"UNARCHIVE"
		);

		updateData(updatedTree);
		if (processedIds.length > 0) {
			addPendingOperation("UNARCHIVE", processedIds);
			message.success(
				`${processedIds.length} elemento${processedIds.length !== 1 ? "s" : ""} desarchivado${processedIds.length !== 1 ? "s" : ""}`
			);
		} else {
			message.info("No se desarchivó ningún elemento");
		}
	};

	const processDeleteAction = (selectedIds) => {
		// Verificar que los elementos puedan ser eliminados
		const flattenedItems = flattenClassifications(data, {
			includeIsInUse: true,
		});

		const notInUseItemsForDelete = flattenedItems.filter(
			(item) =>
				selectedIds.includes(item.id) &&
				!item.isInUse &&
				item.archived === true
		);

		const inUseItems = flattenedItems.filter(
			(item) =>
				selectedIds.includes(item.id) &&
				(item.isInUse || !item.archived)
		);

		if (inUseItems.length > 0) {
			message.info(
				`No se pueden eliminar los siguientes elementos en uso/no archivados: ${inUseItems.map((i) => `"${i.classification_name}"`).join(", ")}`
			);
		}

		if (notInUseItemsForDelete.length === 0) return;

		// Recopilar todos los IDs a eliminar (incluidos hijos)
		const deletedIds = [];
		const recursivelyCollectIds = (nodes, idsToCheck) => {
			nodes.forEach((node) => {
				if (idsToCheck.includes(node.id)) {
					deletedIds.push(node.id);
					// Añadir todos los hijos
					if (node.children && node.children.length > 0) {
						recursivelyCollectIds(
							node.children,
							node.children.map((c) => c.id)
						);
					}
				} else if (node.children && node.children.length > 0) {
					recursivelyCollectIds(node.children, idsToCheck);
				}
			});
		};

		recursivelyCollectIds(
			data,
			notInUseItemsForDelete.map((item) => item.id)
		);

		// Aplicar eliminación al estado
		const processedData = removeNodesById(data, deletedIds);
		updateData(processedData);

		// Registrar operación pendiente
		addPendingOperation("DELETE", deletedIds);
		message.success(`${deletedIds.length} elementos eliminados`);
	};

	return {
		selectedAction,
		setSelectedAction,
		resetData,
		handleCreateCategory,
		handleActionSelected,
	};
};
